import {
  Building2,
  MapPin,
  Phone
} from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { Business, BUSINESS_TYPE_LABELS } from '../../core/models/business'
import ManageButton from '../buttons/ManageButton'
import { getBusinessColor } from '@/core/utils'

interface BusinessCardProps {
  business: Business
  onSelect: (business: Business) => void
  className?: string
}

export default function BusinessCard({
  business,
  onSelect,
  className = ''
}: BusinessCardProps) {
  const { t, isRTL } = useTranslation()

  const isActive = business.isActive

  return (
    <div className={`relative bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200 flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isActive ? `bg-gradient-to-r from-${getBusinessColor(business.color)}-50 to-${getBusinessColor(business.color)}-100` : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{business.name}</h3>
          </div>
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: business.color }}>
              <Building2 className="w-5 h-5 text-white"/>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-5 space-y-4">
        {/* Description */}
        {business.description && (
          <div>
            <p className="text-sm text-gray-600 line-clamp-2">{business.description}</p>
          </div>
        )}

        {/* Business Info */}
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0`} />
            <span className="line-clamp-2 flex-1">{business.address}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone className={`w-4 h-4 flex-shrink-0 ${isRTL ? 'scale-x-[-1]' : ''}`} />
            <span dir="ltr">{business.contactNumber}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">{t('businessType')}:</span>
            <span className="font-medium text-gray-700">
              {BUSINESS_TYPE_LABELS[business.businessType as keyof typeof BUSINESS_TYPE_LABELS] || business.businessType}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${business.isAcceptingOrders
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
              }`}>
              {business.isAcceptingOrders ? t('acceptingOrders') : t('notAcceptingOrders')}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2">
        <ManageButton onClick={onSelect} item={business} className="rounded-md" />
      </div>
    </div>
  )
}
